<template>
  <div
    v-if="isNotEmpty(indexData) && latestChange !== null"
    class="grid grid-cols-10"
  >
    <!-- Index label -->
    <div class="col-span-4 font-semibold" data-testid="more-info-desktop">
      {{ indexName }}
    </div>
    <!-- Index data -->
    <div
      class="col-span-6 grid grid-cols-6 place-content-center gap-3 font-semibold lg:text-sm"
    >
      <PriceAssessmentsSummaryIndexValue
        class="!justify-start"
        :value="latestChange"
      />
      <PriceAssessmentsSummaryIndexValue
        class="!justify-start"
        :value="yearOnYear"
      />
      <PriceAssessmentsSummaryIndexValue
        class="!justify-start"
        :value="yearToDate"
      />

      <div class="flex items-center">{{ today }}</div>
      <div class="flex items-center">{{ last }}</div>
      <TooltipShare
        v-if="!isEmpty(indexData)"
        :twitter-link="twitterLink"
        :linkedin-link="getRouteToShare()"
        :linkedin-shareable-title="`${indexName} Index`"
        :link="getRouteToShare()"
        class="mr-3"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { TRecord } from '~/src/types/common'
import {
  getMarketForRoute,
  getPageForRoute,
  isSpecialMarket,
} from '~/src/utils/markets-with-prices'

const props = defineProps<{
  indexName: string
  indexData: TRecord
  slug: string
}>()

const { $config } = useNuxtApp()

const updateFrequency = computed(() => {
  return props.indexData?.updateFrequency ?? '1M'
})
const latestChange = computed(() => {
  return props.indexData.latestChange?.value
})
const yearToDate = computed(() => {
  return props.indexData.yearToDate?.value
})
const yearOnYear = computed(() => {
  return props.indexData.yearOnYear?.value
})
const today = computed(() => {
  return props.indexData.today?.value ?? 0
})
const last = computed(() => {
  return props.indexData.last?.value ?? 0
})

const postBase = computed(() => {
  const title = props.indexName
  const YOY = yearOnYear.value ? `YOY: ${yearOnYear.value}%` : ''
  const LATEST_CHANGE = latestChange.value
    ? `${updateFrequency.value}: ${latestChange.value}%`
    : ''
  const TODAY = props.indexData.today?.value
    ? `TODAY: ${props.indexData.today?.value ?? 0}`
    : ''
  return `${title} Index
====================
${TODAY}
${LATEST_CHANGE}
${YOY}
`
})

const post = computed(() => {
  const routeForTweet = getRouteToShare()
  const text = `${postBase.value}

Source: @Benchmarkmin
See more at: ${routeForTweet}`
  return encodeURIComponent(text)
})
const twitterLink = computed(() => {
  return `https://twitter.com/intent/tweet?text=${post.value}`
})

function getRoute(
  slug,
  hasSlugMarket = false,
  hasSlugPage = false,
  slugSection = null,
) {
  const market = getMarketForRoute(slug)
  const page = getPageForRoute(slug)
  const slugPage = isSpecialMarket(slug) ? `${page}-prices` : 'prices'
  const path = $config.public.isProduction
    ? $config.public.app.productionUrl
    : window.location.origin
  const routeBase = hasSlugMarket ? `${path}/${market}` : `${path}`
  const route = hasSlugPage ? `${routeBase}/${slugPage}` : routeBase
  return slugSection ? `${route}/${slugSection}` : route
}

function getRouteToShare() {
  if (!props.slug) {
    return ''
  }
  const PRICES_ROUTES = ['lithium-prices', 'lithium-ion-battery-prices']

  if (
    [...PRICES_ROUTES, 'lithium-ion-battery-raw-material-index'].includes(
      props.slug?.toLowerCase(),
    )
  ) {
    const path = getRoute(props.slug)
    let route = `${path}/${props.slug?.toLowerCase()}`
    if (PRICES_ROUTES.includes(props.slug?.toLowerCase())) {
      route = `${route}/?tab=index`
    }
    return route
  }
  return getRoute(props.slug, true, true, 'index')
}

function isNotEmpty(_data) {
  return Object.values(_data).some((_item) => _item !== null && _item !== '')
}

function isEmpty(_data) {
  return !Object.values(_data).some((_item) => _item)
}
</script>
